import { sortFn as rainbowSorter } from 'color-sorter';
import { compact, uniq } from 'lodash';
import { useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';

import Button from 'ui/Button';
import ColorPicker, {
  ColorGroup,
  ColorPickerView,
  parseColor,
} from 'ui/ColorPicker';
import Field from 'ui/Field';
import Icon from 'ui/Icon';
import Tooltip from 'ui/Tooltip';

import { useSankeyFormContext } from '../../contexts/sankeyFormContext';
import { useCorporateBrandingContext, useTeamContext } from '../../utils/team';

type Props = {
  field: SankeyFormColorField;
  label: string;
};

const FormColorSelect = ({ field: name, label }: Props) => {
  const { openCorporateBrandingDrawer } = useCorporateBrandingContext();
  const [colorPickerView, setColorPickerView] =
    useState<ColorPickerView>('preset');

  const { teamSettings } = useTeamContext();
  const brandColors = teamSettings?.brand?.colors ?? [];

  const { form } = useSankeyFormContext();
  const titleFontColorValue = form.watch('title_font_color');
  const currentColorValue = form.watch(name); // Watch current color to update the swatch list properly
  const nodes = form.watch('nodes');

  const presetColorGroups = useMemo((): ColorGroup[] => {
    const brandColorValues = brandColors?.map((s) => s.value);
    const nodeColorValues = Object.values(nodes).map((n) => n.color);
    // Take all the colors appearing in the diagram, make them uppercase, remove
    // duplicates, filter out the standard colors (that will be listed separately)
    // and finally sort them in rainbow order before displaying them to the user.
    const otherColors = uniq(
      compact([titleFontColorValue, ...nodeColorValues]).map((c) =>
        c.toUpperCase()
      )
    )
      .filter((c) => !brandColorValues.includes(c))
      .sort(rainbowSorter)
      .map((value) => ({ value, label: '', keywords: [] }));
    // Put the brand colors first, followed by all the other diagram colors (where new colors can also be added)
    return [
      {
        label: 'Brand colors',
        colors: brandColors,
        actionButton: (
          <Button
            borderRadius="sm"
            boxSize={7}
            color="gray.900"
            onClick={openCorporateBrandingDrawer}
            variant="neutral-ghost"
            size="sm"
          >
            <Icon icon="edit" />
          </Button>
        ),
      },
      {
        label: 'Other diagram colors',
        colors: otherColors,
        actionButton: (
          <Tooltip content="Use different color">
            <Button
              borderRadius="sm"
              boxSize={7}
              color="gray.900"
              onClick={() => setColorPickerView('custom')}
              variant="neutral-ghost"
              size="sm"
            >
              <Icon icon="plus" />
            </Button>
          </Tooltip>
        ),
      },
    ];
  }, [currentColorValue, brandColors, titleFontColorValue, nodes]);

  return (
    <Controller
      name={name}
      control={form.control}
      render={({ field }) => (
        <Field label={label} fontSize="xs" w="fit-content">
          <ColorPicker
            name={field.name}
            value={parseColor(field.value ?? 'black')}
            onValueChange={({ value }) => field.onChange(value.toString('hex'))}
            presetColorGroups={presetColorGroups}
            view={colorPickerView}
            setView={setColorPickerView}
          />
        </Field>
      )}
    />
  );
};

export default FormColorSelect;
